<template>
  <div class="verify-page" :class="{ 'is-standalone': standalone }">

    <!-- Genel sayfa olarak açıldığında başlık burada; Dashboard içinde
         gömülüyken sayfa başlığını kabuk zaten basıyor -->
    <header class="hero" v-if="standalone">
      <div class="hero-inner">
        <span class="hero-badge"><i class="fa fa-shield"></i></span>
        <h1>{{ $t('verify.title') }}</h1>
        <p>{{ $t('verify.subtitle') }}</p>
      </div>
      <LanguageSwitcher class="hero-lang" />
    </header>

    <div class="wrap">

      <!-- Yöntem -->
      <div class="seg method">
        <button :class="{ 'is-active': mode === 'code' }" @click="switchMode('code')">
          <i class="fa fa-keyboard-o"></i> {{ $t('verify.tabCode') }}
        </button>
        <button :class="{ 'is-active': mode === 'file' }" @click="switchMode('file')">
          <i class="fa fa-file-pdf-o"></i> {{ $t('verify.tabFile') }}
        </button>
      </div>

      <!-- Kod ile -->
      <section class="card" v-if="mode === 'code'">
        <p class="lead">{{ $t('verify.codeLead') }}</p>
        <div class="code-row">
          <input class="input is-code" v-model.trim="code"
                 :placeholder="$t('verify.codePlaceholder')"
                 maxlength="40" @keyup.enter="verifyByCode">
          <button class="btn-primary" :class="{ 'is-loading': busy }" @click="verifyByCode">
            {{ $t('verify.check') }}
          </button>
        </div>
        <p class="hint">{{ $t('verify.codeHint') }}</p>
      </section>

      <!-- Dosya ile -->
      <section class="card" v-else>
        <p class="lead">{{ $t('verify.fileLead') }}</p>

        <div class="drop" :class="{ 'is-over': dragging, 'has-file': !!file }"
             @dragover.prevent="dragging = true"
             @dragleave.prevent="dragging = false"
             @drop.prevent="onDrop"
             @click="$refs.fileInput.click()">
          <input class="hidden-input" type="file" accept=".pdf" ref="fileInput" @change="onFilePicked">
          <span class="drop-icon">
            <i class="fa" :class="file ? 'fa-file-pdf-o' : 'fa-cloud-upload'"></i>
          </span>
          <p class="drop-text" v-if="!file">{{ $t('verify.dropHere') }}</p>
          <p class="drop-text" v-else>
            <strong>{{ file.name }}</strong>
            <span class="drop-size">{{ humanSize(file.size) }}</span>
          </p>
        </div>

        <button class="btn-primary is-block" :class="{ 'is-loading': busy }"
                :disabled="!file" @click="verifyByFile">
          {{ $t('verify.check') }}
        </button>
        <p class="hint center">{{ $t('verify.fileHint') }}</p>
      </section>

      <div class="flash is-err" v-if="errorMessage">
        <span>{{ errorMessage }}</span>
        <button class="flash-close" @click="errorMessage = ''">&times;</button>
      </div>

      <!-- Sonuç -->
      <template v-if="result">

        <!-- Karar -->
        <section class="verdict" :class="'is-' + verdict.kind">
          <span class="verdict-icon"><i class="fa" :class="verdictIcon"></i></span>
          <div class="verdict-text">
            <h2>{{ verdict.title }}</h2>
            <p>{{ verdict.text }}</p>
          </div>
        </section>

        <!-- Bölümleri topluca aç / kapat -->
        <div class="section-tools">
          <button class="link-btn" type="button" @click="setAll(!allOpen)">
            <i class="fa" :class="allOpen ? 'fa-compress' : 'fa-expand'"></i>
            {{ allOpen ? $t('verify.collapseAll') : $t('verify.expandAll') }}
          </button>
        </div>

        <!-- Dijital imza -->
        <section class="card" v-if="result.signature">
          <button class="card-toggle" type="button" @click="toggle('sectionSignature')"
                  :aria-expanded="isOpen('sectionSignature') ? 'true' : 'false'">
            <h3 class="card-title">{{ $t('verify.sectionSignature') }}</h3>
            <i class="fa fa-chevron-down chevron" :class="{ 'is-open': isOpen('sectionSignature') }"></i>
          </button>
          <div class="card-body" v-show="isOpen('sectionSignature')">

            <ul class="checks">
              <li>
                <span class="check-label">{{ $t('verify.rowSigned') }}</span>
                <Mark :ok="result.signature.signed" />
              </li>
              <li v-if="result.signature.signed">
                <span class="check-label">
                  {{ $t('verify.rowUnmodified') }}
                  <small>{{ $t('verify.rowUnmodifiedHint') }}</small>
                </span>
                <Mark :ok="result.signature.digestMatches" />
              </li>
              <li v-if="result.signature.signed">
                <span class="check-label">
                  {{ $t('verify.rowCovers') }}
                  <small v-if="!result.signature.coversWholeDocument">
                    {{ $t('verify.appendedHint') }}
                  </small>
                </span>
                <!-- Meşru artımlı güncelleme de olumlu sayılıyor -->
                <Mark :ok="result.signature.coversWholeDocument
                           || result.signature.appendedIsValidationData" />
              </li>
              <li v-if="result.signature.signed && result.signature.appendedAfterSignature">
                <span class="check-label">{{ $t('verify.rowAppended') }}</span>
                <span class="pill" :class="result.signature.appendedIsValidationData ? 'is-info' : 'is-no'">
                  {{ appendedLabel }}
                </span>
              </li>
              <li v-if="result.signature.signed">
                <span class="check-label">{{ $t('verify.rowCertValid') }}</span>
                <Mark :ok="!result.signature.certificateExpired" />
              </li>
            </ul>

            <dl class="facts" v-if="result.signature.signerName">
              <div class="fact">
                <dt>{{ $t('verify.rowSigner') }}</dt>
                <dd>{{ result.signature.signerName }}</dd>
              </div>
              <div class="fact" v-if="result.signature.certificateValidTo">
                <dt>{{ $t('verify.rowCertPeriod') }}</dt>
                <dd>
                  {{ formatDay(result.signature.certificateValidFrom) }} –
                  {{ formatDay(result.signature.certificateValidTo) }}
                </dd>
              </div>
            </dl>

            <div class="note is-warn" v-if="result.signature.selfSigned">
              <strong>{{ $t('verify.selfSignedTitle') }}</strong>
              <span>{{ $t('verify.selfSignedText') }}</span>
            </div>
          </div>
        </section>

        <!-- Sertifika zinciri -->
        <section class="card" v-if="signature && signature.chain && signature.chain.length">
          <button class="card-toggle" type="button" @click="toggle('sectionChain')"
                  :aria-expanded="isOpen('sectionChain') ? 'true' : 'false'">
            <h3 class="card-title">{{ $t('verify.sectionChain') }}</h3>
            <i class="fa fa-chevron-down chevron" :class="{ 'is-open': isOpen('sectionChain') }"></i>
          </button>
          <div class="card-body" v-show="isOpen('sectionChain')">

            <ol class="chain">
              <li v-for="cert in signature.chain" :key="cert.position">
                <span class="chain-dot" :class="chainDotClass(cert)"></span>

                <div class="chain-text">
                  <div class="chain-head">
                    <strong>{{ cert.subject }}</strong>
                    <span class="pill is-idle">{{ chainRole(cert) }}</span>
                    <span class="pill is-warn" v-if="cert.isSigner && cert.isRoot">
                      {{ $t('verify.chainSelfSigned') }}
                    </span>
                  </div>

                  <dl class="chain-facts">
                    <div>
                      <dt>{{ $t('verify.chainIssuer') }}</dt>
                      <dd>{{ cert.issuer }}</dd>
                    </div>
                    <div>
                      <dt>{{ $t('verify.chainValidity') }}</dt>
                      <dd>{{ formatDay(cert.validFrom) }} – {{ formatDay(cert.validTo) }}</dd>
                    </div>
                    <div>
                      <dt>{{ $t('verify.chainSerial') }}</dt>
                      <dd><code>{{ cert.serialNumber }}</code></dd>
                    </div>
                    <div>
                      <dt>{{ $t('verify.chainRevocation') }}</dt>
                      <dd :class="{ 'is-muted': !cert.hasOcspUrl && !cert.hasCrlUrl }">
                        {{ revocationLabel(cert) }}
                      </dd>
                    </div>
                  </dl>
                </div>
              </li>
            </ol>

            <p class="hint mt">{{ $t('verify.chainHint') }}</p>
          </div>
        </section>

        <!-- LTV -->
        <section class="card" v-if="signature && signature.signed">
          <button class="card-toggle" type="button" @click="toggle('sectionLtv')"
                  :aria-expanded="isOpen('sectionLtv') ? 'true' : 'false'">
            <h3 class="card-title">{{ $t('verify.sectionLtv') }}</h3>
            <i class="fa fa-chevron-down chevron" :class="{ 'is-open': isOpen('sectionLtv') }"></i>
          </button>
          <div class="card-body" v-show="isOpen('sectionLtv')">

            <ul class="checks">
              <li>
                <span class="check-label">{{ $t('verify.ltvLevel') }}</span>
                <span class="pill" :class="ltv.level ? 'is-ok' : 'is-idle'">
                  {{ ltv.level ? 'PAdES-B-' + ltv.level : $t('verify.ltvNone') }}
                </span>
              </li>
              <li>
                <span class="check-label">{{ $t('verify.ltvRevocationEmbedded') }}</span>
                <Mark :ok="!!ltv.hasRevocationData" />
              </li>
              <li>
                <span class="check-label">{{ $t('verify.ltvDocTimestamp') }}</span>
                <Mark :ok="!!ltv.hasDocumentTimeStamp" />
              </li>
            </ul>

            <div class="note" :class="ltv.level === 'LTA' ? 'is-ok' : 'is-warn'">
              {{ ltvText }}
            </div>
          </div>
        </section>

        <!-- Zaman damgası -->
        <section class="card" v-if="timestampAt || (result.signature && result.signature.signed)">
          <button class="card-toggle" type="button" @click="toggle('sectionTimestamp')"
                  :aria-expanded="isOpen('sectionTimestamp') ? 'true' : 'false'">
            <h3 class="card-title">{{ $t('verify.sectionTimestamp') }}</h3>
            <i class="fa fa-chevron-down chevron" :class="{ 'is-open': isOpen('sectionTimestamp') }"></i>
          </button>
          <div class="card-body" v-show="isOpen('sectionTimestamp')">
            <template v-if="timestampAt">
              <p class="stamp">{{ formatDate(timestampAt) }}</p>

              <!-- Damgayı kimin verdiği, tarihin kendisi kadar önemli:
                   zamanın dayandığı otorite burada görünüyor -->
              <dl class="facts" v-if="signature">
                <div class="fact is-wide">
                  <dt>{{ $t('verify.tsAuthority') }}</dt>
                  <dd v-if="signature.timestampAuthority">{{ signature.timestampAuthority }}</dd>
                  <dd class="is-muted" v-else>{{ $t('verify.tsNoAuthority') }}</dd>
                </div>
                <div class="fact" v-if="signature.timestampAuthorityIssuer">
                  <dt>{{ $t('verify.tsAuthorityIssuer') }}</dt>
                  <dd>{{ signature.timestampAuthorityIssuer }}</dd>
                </div>
                <div class="fact" v-if="signature.timestampSerial">
                  <dt>{{ $t('verify.tsSerial') }}</dt>
                  <dd><code>{{ signature.timestampSerial }}</code></dd>
                </div>
              </dl>

              <ul class="checks mt" v-if="signature && signature.timestampMatchesSignature !== null">
                <li>
                  <span class="check-label">
                    {{ $t('verify.tsMatches') }}
                    <small>{{ $t('verify.tsMatchesHint') }}</small>
                  </span>
                  <Mark :ok="signature.timestampMatchesSignature" />
                </li>
              </ul>

              <p class="hint mt">{{ $t('verify.timestampHint') }}</p>
            </template>
            <p class="hint" v-else>{{ $t('verify.noTimestamp') }}</p>
          </div>
        </section>

        <!-- Kayıt -->
        <section class="card" v-if="record">
          <button class="card-toggle" type="button" @click="toggle('sectionRecord')"
                  :aria-expanded="isOpen('sectionRecord') ? 'true' : 'false'">
            <h3 class="card-title">{{ $t('verify.sectionRecord') }}</h3>
            <i class="fa fa-chevron-down chevron" :class="{ 'is-open': isOpen('sectionRecord') }"></i>
          </button>
          <div class="card-body" v-show="isOpen('sectionRecord')">

            <dl class="facts">
              <div class="fact is-wide">
                <dt>{{ $t('verify.rowSubject') }}</dt>
                <dd>{{ record.subject }}</dd>
              </div>
              <div class="fact" v-if="record.documentName">
                <dt>{{ $t('verify.rowDocument') }}</dt>
                <dd>{{ record.documentName }}</dd>
              </div>
              <div class="fact">
                <dt>{{ $t('verify.rowStatus') }}</dt>
                <dd><span class="pill" :class="statusPill(record.status)">{{ statusLabel(record.status) }}</span></dd>
              </div>
              <div class="fact" v-if="record.completedAt">
                <dt>{{ $t('verify.rowCompleted') }}</dt>
                <dd>{{ formatDate(record.completedAt) }}</dd>
              </div>
              <div class="fact" v-if="record.documentId">
                <dt>{{ $t('verify.rowDocumentId') }}</dt>
                <dd><code>{{ record.documentId }}</code></dd>
              </div>
              <div class="fact" v-if="record.verifyCode">
                <dt>{{ $t('verify.rowCode') }}</dt>
                <dd><code>{{ record.verifyCode }}</code></dd>
              </div>
            </dl>

            <ul class="checks mt">
              <li>
                <span class="check-label">
                  {{ $t('verify.rowChain') }}
                  <small>{{ $t('verify.rowChainHint') }}</small>
                </span>
                <Mark :ok="record.auditValid" />
              </li>
            </ul>

            <h4 class="sub-title">{{ $t('verify.sectionSigners') }}</h4>
            <ul class="rows">
              <li class="row" v-for="s in record.signers" :key="s.email">
                <div class="row-text">
                  <strong>{{ s.name }}</strong>
                  <span class="row-sub">{{ s.email }}</span>
                </div>
                <div class="row-side">
                  <span class="row-date">{{ s.signedAt ? formatDate(s.signedAt) : '—' }}</span>
                  <span class="pill" :class="signerPill(s.status)">{{ signerLabel(s.status) }}</span>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <!-- Kayıtla eşleşme -->
        <section class="card" v-if="mode === 'file' && result.fileChecksum">
          <button class="card-toggle" type="button" @click="toggle('sectionMatch')"
                  :aria-expanded="isOpen('sectionMatch') ? 'true' : 'false'">
            <h3 class="card-title">{{ $t('verify.sectionMatch') }}</h3>
            <i class="fa fa-chevron-down chevron" :class="{ 'is-open': isOpen('sectionMatch') }"></i>
          </button>
          <div class="card-body" v-show="isOpen('sectionMatch')">
            <ul class="checks">
              <li>
                <span class="check-label">
                  {{ result.knownDocument ? $t('verify.matchYes') : $t('verify.matchNo') }}
                </span>
                <Mark :ok="result.knownDocument" />
              </li>
            </ul>
            <p class="hint mt">{{ $t('verify.rowChecksum') }}</p>
            <p class="digest">{{ result.fileChecksum }}</p>
          </div>
        </section>

        <!-- Adobe ile bağımsız kontrol -->
        <section class="card is-quiet">
          <button class="card-toggle" type="button" @click="toggle('sectionAdobe')"
                  :aria-expanded="isOpen('sectionAdobe') ? 'true' : 'false'">
            <h3 class="card-title">{{ $t('verify.sectionAdobe') }}</h3>
            <i class="fa fa-chevron-down chevron" :class="{ 'is-open': isOpen('sectionAdobe') }"></i>
          </button>
          <div class="card-body" v-show="isOpen('sectionAdobe')">
            <p class="hint">{{ $t('verify.adobeLead') }}</p>

            <ol class="steps">
              <li v-for="(text, i) in adobeSteps" :key="i">
                <span class="step-no">{{ i + 1 }}</span>
                <span>{{ text }}</span>
              </li>
            </ol>

            <div class="note is-info">{{ $t('verify.adobeNote') }}</div>
          </div>
        </section>
      </template>
    </div>

    <footer class="page-foot" v-if="standalone">
      <strong>{{ $t('verify.footer') }}</strong>
    </footer>
  </div>
</template>

<script>
import axios from 'axios'
import Defaults from '../config/Defaults'
import LanguageSwitcher from './LanguageSwitcher.vue'

// Evet/hayır rozeti. Scoped CSS yalnızca alt bileşenin KÖK elemanına
// uygulanıyor, o yüzden tek kök: ikon global FontAwesome sınıfıyla geliyor.
const Mark = {
  props: { ok: Boolean },
  template: '<span class="pill" :class="ok ? \'is-ok\' : \'is-no\'">' +
            '<i class="fa" :class="ok ? \'fa-check\' : \'fa-times\'"></i> ' +
            '{{ ok ? $t(\'common.yes\') : $t(\'common.no\') }}</span>'
};

export default {
  name: 'Verify',
  components: { Mark, LanguageSwitcher },
  props: {
    // Varsayılan adres Defaults'tan geliyor: frontend ile backend farklı
    // origin'deyse '/api' frontend sunucusuna gider, 404 döner ve arayüz
    // bunu "belge bulunamadı" diye gösterir.
    apiBase: {
      type: String,
      default: function () {
        try {
          return Defaults.applications().backEnd.url + '/api';
        } catch (e) {
          return '/api';
        }
      }
    },
    // Dashboard içinde gömülüyken başlık ve alt bilgi gizleniyor;
    // kabuk zaten sayfa başlığını basıyor
    standalone: { type: Boolean, default: false }
  },
  data() {
    return {
      // Açık bölümler. İlk bakışta en çok sorulan üçü açık: imza,
      // zaman damgası ve kayıt. Zincir, LTV ve Adobe rehberi ayrıntı
      // arayana; kapalı başlayıp sayfayı kısa tutuyor.
      openSections: {
        sectionSignature: true,
        sectionTimestamp: true,
        sectionRecord: true,
        sectionMatch: true,
        sectionChain: false,
        sectionLtv: false,
        sectionAdobe: false
      },

      mode: 'code',

      code: '',
      file: null,
      dragging: false,

      busy: false,
      errorMessage: '',
      result: null
    };
  },
  computed: {
    allOpen() {
      var o = this.openSections;
      return Object.keys(o).every(function (k) { return o[k]; });
    },
    signature() {
      return this.result ? this.result.signature : null;
    },
    ltv() {
      return (this.signature && this.signature.ltv) || {};
    },
    appendedLabel() {
      var sig = this.signature;
      if (!sig || !sig.appendedAfterSignature) return this.$t('verify.appendedNone');
      var key = sig.appendedIsValidationData ? 'appendedValidation' : 'appendedContent';
      return this.$t('verify.' + key, { bytes: sig.appendedAfterSignature });
    },
    ltvText() {
      if (this.ltv.level === 'LTA') return this.$t('verify.ltvGoodText');
      if (this.ltv.level === 'LT') return this.$t('verify.ltvLtText');
      return this.$t('verify.ltvNoneText');
    },
    // Adım metinleri sözlükte ayrı anahtarlar; tek dizide toplanıyor
    adobeSteps() {
      var self = this;
      return [1, 2, 3, 4, 5, 6].map(function (n) {
        return self.$t('verify.adobeStep' + n);
      });
    },
    record() {
      return this.result ? this.result.record : null;
    },
    // Kod sorgusunda damga kayıttan, dosya sorgusunda imzanın içinden gelir
    timestampAt() {
      if (!this.result) return null;
      if (this.result.signature && this.result.signature.timestampedAt) {
        return this.result.signature.timestampedAt;
      }
      return this.record ? this.record.timestampedAt : null;
    },

    // Tek cümlelik karar. Dosya yüklendiyse imzaya, yoksa kayda bakar.
    verdict() {
      const signature = this.result ? this.result.signature : null;
      if (signature) {
        if (!signature.signed) {
          return { kind: 'unsigned', title: this.$t('verify.verdictUnsigned'), text: this.$t('verify.verdictUnsignedText') };
        }
        // İmzadan sonra eklenen bölüm yalnızca doğrulama verisiyse
        // (DocuSign LTV'yi böyle ekliyor) bu bozulma değil. Sunucu bunu
        // appendedIsValidationData ile bildiriyor.
        var coverageOk = signature.coversWholeDocument || signature.appendedIsValidationData;
        if (!signature.digestMatches || !coverageOk) {
          return { kind: 'broken', title: this.$t('verify.verdictBroken'), text: this.$t('verify.verdictBrokenText') };
        }
        return { kind: 'valid', title: this.$t('verify.verdictValid'), text: this.$t('verify.verdictValidText') };
      }
      return { kind: 'record', title: this.$t('verify.verdictRecord'), text: this.$t('verify.verdictRecordText') };
    },
    verdictClass() {
      return {
        valid: 'is-success', broken: 'is-danger',
        unsigned: 'is-warning', record: 'is-info'
      }[this.verdict.kind] + ' is-light';
    },
    verdictIcon() {
      return {
        valid: 'fa-check-circle', broken: 'fa-exclamation-triangle',
        unsigned: 'fa-question-circle', record: 'fa-info-circle'
      }[this.verdict.kind];
    }
  },
  mounted() {
    // Adresteki kodla doğrudan açılabilsin: #/dogrula/A7K2-9MPX-3RTQ
    var fromUrl = (window.location.hash.match(/#\/(?:dogrula|verify)\/([A-Z0-9-]+)/i) || [])[1];
    if (fromUrl) {
      this.code = fromUrl.toUpperCase();
      this.verifyByCode();
    }
  },
  methods: {
    isOpen(key) {
      return !!this.openSections[key];
    },
    toggle(key) {
      this.$set(this.openSections, key, !this.openSections[key]);
    },
    setAll(value) {
      Object.keys(this.openSections).forEach((k) => this.$set(this.openSections, k, value));
    },

    switchMode(mode) {
      this.mode = mode;
      this.result = null;
      this.errorMessage = '';
    },

    verifyByCode() {
      const code = String(this.code || '').toUpperCase();
      this.errorMessage = '';
      this.result = null;

      if (!code) { this.errorMessage = this.$t('verify.emptyCode'); return; }
      if (this.busy) return;

      this.busy = true;
      axios.get(this.apiBase + '/verify/' + encodeURIComponent(code), { timeout: 15000 })
        .then((response) => {
          const body = response.data || {};
          if (body.code === 100 && body.record) {
            this.result = { record: body.record, signature: null };
          } else {
            this.errorMessage = body.message || this.$t('verify.notFound');
          }
        })
        .catch((error) => { this.errorMessage = this.describe(error); })
        .then(() => { this.busy = false; });
    },

    verifyByFile() {
      this.errorMessage = '';
      this.result = null;

      if (!this.file) { this.errorMessage = this.$t('verify.noFile'); return; }
      if (this.busy) return;

      this.busy = true;
      const data = new FormData();
      data.append('file', this.file);

      axios.post(this.apiBase + '/verify', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      })
        .then((response) => {
          const body = response.data || {};
          if (body.code === 100) {
            this.result = body;
          } else {
            this.errorMessage = body.message || this.$t('verify.serverError');
          }
        })
        .catch((error) => { this.errorMessage = this.describe(error); })
        .then(() => { this.busy = false; });
    },

    // error.response kontrolsüz okunursa ağ koptuğunda sayfa çöker
    describe(error) {
      if (!error.response) return this.$t('verify.networkError');

      const data = error.response.data;
      // Sunucu JSON döndüyse mesajı ona ait; HTML döndüyse istek yanlış
      // adrese gitmiş demektir ve "belge bulunamadı" demek yanıltıcı olur
      if (data && typeof data === 'object' && data.message) return data.message;
      if (error.response.status === 404) {
        return this.$t('verify.serverError') + ' (404: ' + this.apiBase + ')';
      }
      return this.$t('verify.serverError');
    },

    onDrop(event) {
      this.dragging = false;
      const dropped = event.dataTransfer.files[0];
      if (dropped) this.acceptFile(dropped);
    },
    onFilePicked(event) {
      const picked = event.target.files[0];
      if (picked) this.acceptFile(picked);
    },
    acceptFile(picked) {
      this.errorMessage = '';
      this.result = null;
      if (picked.size > 20 * 1024 * 1024) { this.errorMessage = this.$t('templates.uploadTooBig'); return; }
      if (!/\.pdf$/i.test(picked.name)) { this.errorMessage = this.$t('verify.notPdf'); return; }
      this.file = picked;
    },

    statusLabel(status) {
      const map = { draft: 'statusDraft', sent: 'statusSent', in_progress: 'statusProgress',
        completed: 'statusCompleted', declined: 'statusDeclined', voided: 'statusVoided' };
      return map[status] ? this.$t('envelopes.' + map[status]) : status;
    },
    chainRole(cert) {
      if (cert.isSigner) return this.$t('verify.chainSigner');
      if (cert.isRoot) return this.$t('verify.chainRoot');
      return this.$t('verify.chainIntermediate');
    },
    chainDotClass(cert) {
      if (cert.isSigner) return 'is-signer';
      if (cert.isRoot) return 'is-root';
      return '';
    },
    // İptal adresi LTV'nin ön koşulu: yoksa gömülecek veri de üretilemiyor
    revocationLabel(cert) {
      var parts = [];
      if (cert.hasOcspUrl) parts.push('OCSP');
      if (cert.hasCrlUrl) parts.push('CRL');
      return parts.length ? parts.join(' + ') : this.$t('verify.chainNoRevocation');
    },

    statusPill(status) {
      return {
        draft: 'is-idle', sent: 'is-info', in_progress: 'is-warn',
        completed: 'is-ok', declined: 'is-no', voided: 'is-dark'
      }[status] || 'is-idle';
    },
    signerLabel(status) {
      const map = { pending: 'rcpPending', sent: 'rcpSent', viewed: 'rcpViewed',
        signed: 'rcpSigned', declined: 'rcpDeclined' };
      return map[status] ? this.$t('envelopes.' + map[status]) : status;
    },
    signerPill(status) {
      return {
        pending: 'is-idle', sent: 'is-info', viewed: 'is-warn',
        signed: 'is-ok', declined: 'is-no'
      }[status] || 'is-idle';
    },

    formatDate(value) {
      return value ? this.$formatDate(value) : '';
    },
    formatDay(value) {
      return value ? this.$formatDate(value, false) : '';
    },
    humanSize(bytes) {
      if (!bytes) return '';
      const units = ['B', 'KB', 'MB'];
      let i = 0, n = bytes;
      while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
      return n.toFixed(i === 0 ? 0 : 1) + ' ' + units[i];
    }
  }
}
</script>
<style scoped>
.verify-page { width: 100%; }

.wrap {
  max-width: 720px;
  margin: 0 auto;
}

.is-standalone { background: #f6f7fb; min-height: 100vh; }
.is-standalone .wrap { padding: 0 20px 40px; }

/* --- Genel sayfa başlığı --- */
.hero {
  position: relative;
  background: linear-gradient(125deg, #1d1d38 0%, #2f2f5a 55%, #3a3d75 100%);
  padding: 46px 20px 76px;
  margin-bottom: -42px;
  text-align: center;
  overflow: hidden;
}

.hero::after {
  content: "";
  position: absolute;
  right: -60px;
  top: -110px;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 209, 178, 0.26) 0%, rgba(0, 209, 178, 0) 70%);
}

.hero-inner { position: relative; z-index: 1; }

.hero-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: linear-gradient(140deg, #00d1b2 0%, #00a88f 100%);
  color: #06302a;
  font-size: 18px;
  margin-bottom: 14px;
}

.hero h1 { font-size: 25px; font-weight: 700; color: #fff; }

.hero p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.66);
  margin-top: 6px;
  max-width: 460px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

.hero-lang { position: absolute; top: 18px; right: 20px; z-index: 2; color: #fff; }

/* --- Yöntem seçimi --- */
.seg {
  display: flex;
  background: #eceef6;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.seg button {
  flex: 1 1 0;
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13.5px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.seg button.is-active {
  background: #fff;
  color: #0b6e5f;
  box-shadow: 0 1px 3px rgba(24, 24, 48, 0.14);
}

.seg button i { margin-right: 5px; opacity: 0.8; }

.method { position: relative; z-index: 2; margin-bottom: 16px; }
.is-standalone .method { background: #fff; box-shadow: 0 6px 20px rgba(24, 24, 48, 0.1); }

/* --- Kart --- */
.card {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 22px 24px;
  margin-bottom: 16px;
}

.card.is-quiet { background: #fafbfe; }

.card-title {
  position: relative;
  font-size: 15px;
  font-weight: 700;
  color: #1e2233;
  padding-left: 12px;
  margin-bottom: 14px;
}

.card-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 3px;
  bottom: 3px;
  width: 3px;
  border-radius: 2px;
  background: #00d1b2;
}

.sub-title {
  font-size: 13px;
  font-weight: 700;
  color: #5a6180;
  margin: 20px 0 10px;
}

.lead { font-size: 14px; color: #79809a; margin-bottom: 14px; line-height: 1.6; }

.hint { font-size: 12.5px; color: #98a0b5; line-height: 1.6; }
.hint.center { text-align: center; margin-top: 10px; }
.hint.mt { margin-top: 14px; }

/* --- Kod girişi --- */
.code-row { display: flex; gap: 10px; }

.input {
  flex: 1 1 auto;
  min-width: 0;
  height: 46px;
  border: 1px solid #dde1ea;
  border-radius: 9px;
  padding: 0 14px;
  font-size: 15px;
  color: #2b3146;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.input.is-code {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

/* --- Dosya bırakma --- */
.drop {
  border: 2px dashed #d5dae6;
  border-radius: 12px;
  padding: 30px 16px;
  text-align: center;
  cursor: pointer;
  background: #fbfcfe;
  transition: border-color 0.15s, background-color 0.15s;
}

.drop:hover,
.drop.is-over { border-color: #00b89c; background: #f2fcfa; }
.drop.has-file { border-style: solid; border-color: #bfe8df; background: #f4fdfa; }

.hidden-input { display: none; }

.drop-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: #eef1f8;
  color: #8b93a8;
  font-size: 19px;
  margin-bottom: 10px;
}

.drop.has-file .drop-icon { background: #e0f6f0; color: #0f8d78; }

.drop-text { font-size: 13.5px; color: #5a6180; }
.drop-size { color: #98a0b5; margin-left: 6px; font-size: 12.5px; }

/* --- Karar --- */
.verdict {
  display: flex;
  align-items: center;
  gap: 15px;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 16px;
  border: 1px solid transparent;
}

.verdict-icon {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  background: rgba(255, 255, 255, 0.65);
}

.verdict-text h2 { font-size: 16.5px; font-weight: 700; line-height: 1.3; }
.verdict-text p { font-size: 13px; margin-top: 3px; line-height: 1.55; opacity: 0.9; }

.verdict.is-valid { background: #e6f8f1; border-color: #bfe8db; color: #0c5c4c; }
.verdict.is-broken { background: #fdeceb; border-color: #f4cfcc; color: #9e2f2a; }
.verdict.is-unsigned { background: #fdf5e6; border-color: #f2e0bd; color: #8a6116; }
.verdict.is-record { background: #eaf1fd; border-color: #cfdcf6; color: #24508f; }

/* --- Kontrol satırları --- */
.checks {
  border: 1px solid #eef0f6;
  border-radius: 10px;
  overflow: hidden;
}

.checks.mt { margin-top: 14px; }

.checks li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 15px;
  border-bottom: 1px solid #f1f3f8;
}

.checks li:last-child { border-bottom: 0; }

.check-label {
  flex: 1 1 auto;
  font-size: 13.5px;
  color: #2b3146;
}

.check-label small {
  display: block;
  font-size: 12px;
  color: #98a0b5;
  margin-top: 2px;
  line-height: 1.5;
}

/* --- Bilgi ızgarası --- */
.facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  background: #eef0f6;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 14px;
}

.fact { background: #fff; padding: 12px 15px; }
.fact.is-wide { grid-column: 1 / -1; }

.fact dt {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1b5;
  margin-bottom: 4px;
}

.fact dd { font-size: 13.5px; color: #2b3146; word-break: break-word; }

.fact code,
.digest {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  color: #4a5175;
  background: #f4f6fb;
  border-radius: 5px;
  padding: 2px 7px;
}

.digest {
  display: block;
  margin-top: 6px;
  padding: 10px 12px;
  word-break: break-all;
  line-height: 1.6;
}

/* --- İmzalayan satırları --- */
.rows { border: 1px solid #eef0f6; border-radius: 10px; overflow: hidden; }

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 15px;
  border-bottom: 1px solid #f1f3f8;
}

.row:last-child { border-bottom: 0; }

.row-text { display: flex; flex-direction: column; min-width: 0; }
.row-text strong { font-size: 13.5px; color: #22283a; font-weight: 600; }
.row-sub { font-size: 12.5px; color: #8b93a8; word-break: break-word; }

.row-side { display: flex; align-items: center; gap: 10px; flex: 0 0 auto; }
.row-date { font-size: 12px; color: #98a0b5; font-variant-numeric: tabular-nums; }

/* --- Rozetler --- */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.pill.is-ok { background: #e4f6ef; color: #0f6e5c; }
.pill.is-no { background: #fdeceb; color: #a8322c; }
.pill.is-idle { background: #f2f4fa; color: #7a819a; }
.pill.is-info { background: #e8effc; color: #2d5eaa; }
.pill.is-warn { background: #fdf2e2; color: #a66a12; }
.pill.is-dark { background: #e9eaf0; color: #5a6180; }

.stamp {
  font-size: 15px;
  font-weight: 600;
  color: #1e2233;
  font-variant-numeric: tabular-nums;
  margin-bottom: 6px;
}

/* --- Sertifika zinciri --- */
/* Dikey çizgi zincirin sırasını gösteriyor: imzalayandan köke */
.chain {
  position: relative;
  list-style: none;
  padding-left: 4px;
}

.chain li {
  position: relative;
  padding: 0 0 16px 20px;
  border-left: 2px solid #eef0f6;
}

.chain li:last-child { border-left-color: transparent; padding-bottom: 0; }

.chain-dot {
  position: absolute;
  left: -7px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #c8cfe0;
  box-shadow: 0 0 0 3px #fff;
}

.chain-dot.is-signer { background: #00c2a4; }
.chain-dot.is-root { background: #7d86b4; }

.chain-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 7px;
}

.chain-head strong { font-size: 14px; color: #1e2233; }

.chain-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px 16px;
}

.chain-facts dt {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1b5;
  margin-bottom: 2px;
}

.chain-facts dd {
  font-size: 12.5px;
  color: #3c4257;
  word-break: break-word;
}

.chain-facts dd.is-muted { color: #a7aec1; }

.chain-facts code {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11.5px;
  background: #f4f6fb;
  border-radius: 4px;
  padding: 1px 6px;
}

/* --- Adobe adımları --- */
.steps {
  list-style: none;
  counter-reset: none;
  margin: 14px 0 0;
  padding: 0;
}

.steps li {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 9px 0;
  font-size: 13.5px;
  color: #3c4257;
  line-height: 1.6;
  border-bottom: 1px solid #f1f3f8;
}

.steps li:last-child { border-bottom: 0; }

.step-no {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #22223d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  font-weight: 700;
  margin-top: 1px;
}

/* --- Not --- */
.note {
  display: block;
  border-radius: 10px;
  padding: 13px 15px;
  margin-top: 14px;
  font-size: 12.5px;
  line-height: 1.6;
}

.note strong { display: block; margin-bottom: 3px; }
.note.is-warn { background: #fdf5e6; color: #7d5713; }
.note.is-ok { background: #e6f8f1; color: #0c5c4c; }
.note.is-info { background: #eaf1fd; color: #24508f; margin-top: 16px; }

/* --- Bildirim --- */
.flash {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 16px;
  background: #fdeceb;
  color: #a8322c;
}

.flash-close {
  background: transparent;
  border: 0;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}

/* --- Düğmeler --- */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid transparent;
  border-radius: 9px;
  padding: 0 20px;
  height: 46px;
  font-size: 14px;
  font-weight: 600;
  color: #06302a;
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  box-shadow: 0 3px 10px rgba(0, 168, 143, 0.28);
  cursor: pointer;
  transition: background-color 0.15s, opacity 0.15s;
}

.btn-primary:hover { background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%); }
.btn-primary.is-block { width: 100%; margin-top: 16px; }
.btn-primary.is-loading { opacity: 0.7; pointer-events: none; }

.btn-primary:disabled {
  background: #e7eaf2;
  color: #a7aec1;
  box-shadow: none;
  cursor: not-allowed;
}

.page-foot {
  text-align: center;
  padding: 26px 0 34px;
  color: #98a0b5;
  font-size: 13px;
}

@media screen and (max-width: 640px) {
  .facts { grid-template-columns: 1fr; }
  .chain-facts { grid-template-columns: 1fr; }
  .code-row { flex-direction: column; }
  .btn-primary { width: 100%; }
  .row { flex-wrap: wrap; }
  .hero { padding: 38px 18px 70px; }
}

/* --- Açılır kapanır bölümler --- */
.card-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
}

/* Başlık kapalıyken alt boşluk bırakmasın */
.card-toggle .card-title { margin-bottom: 0; }
.card-body { margin-top: 14px; }

.chevron {
  flex: 0 0 auto;
  font-size: 13px;
  color: #a7aec1;
  transition: transform 0.2s ease, color 0.15s;
}

.chevron.is-open { transform: rotate(180deg); }
.card-toggle:hover .chevron { color: #0b6e5f; }

.card-toggle:focus-visible {
  outline: 2px solid #00b89c;
  outline-offset: 4px;
  border-radius: 6px;
}

.section-tools {
  display: flex;
  justify-content: flex-end;
  margin: -4px 0 10px;
}

.link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 0;
  padding: 4px 2px;
  font-size: 12.5px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
}

.link-btn:hover { color: #0b6e5f; }
</style>


// WEBPACK FOOTER //
// src/Components/Verify.vue